import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, formatDate, useListState, sortRows, SortHeader } from "@/components/prospects/shared";
import { MagnifyingGlass, CircleNotch, Handshake } from "@phosphor-icons/react";

const ANY = "__any__";

// Clients are NOT a separate entity: this is the same prospect/organization
// record filtered on lifecycle_status === "client" (set by Closed-Won).
// Opening a row goes to the existing organization detail — full history,
// contacts, properties, activities, opportunities and audit trail preserved.
export default function Clients() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [ls, setLs] = useListState("clients", {
    q: "", salespersonId: ANY, status: ANY, sortKey: "organization", sortDir: "asc",
  });
  const { q, salespersonId, status } = ls;
  const setQ = (v) => setLs((s) => ({ ...s, q: v }));
  const setSalespersonId = (v) => setLs((s) => ({ ...s, salespersonId: v }));
  const setStatus = (v) => setLs((s) => ({ ...s, status: v }));
  const onSort = (key) => setLs((s) => ({
    ...s, sortKey: key, sortDir: s.sortKey === key && s.sortDir === "asc" ? "desc" : "asc",
  }));
  const sorted = rows ? sortRows(rows, ls.sortKey, ls.sortDir) : null;

  // Fetch lives inside the effect; deps are exactly the reactive inputs the
  // request depends on (q, salespersonId, status). ANY is a module constant
  // (stable, not a dependency). The `active` flag cancels stale responses so
  // an unmounted component is never updated. No lint suppression needed.
  useEffect(() => {
    let active = true;
    api.get("/prospects", {
      params: {
        q: q.trim() || undefined,
        salesperson_id: salespersonId === ANY ? undefined : salespersonId,
        status: status === ANY ? undefined : status,
        lifecycle_status: "client",
      },
    })
      .then((r) => { if (active) setRows(r.data); })
      .catch((e) => { if (active) toast.error(formatApiError(e.response?.data?.detail)); });
    return () => { active = false; };
  }, [q, salespersonId, status]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    api.get("/users/options")
      .then((r) => { if (active) setUsers(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [isAdmin]);

  return (
    <div className="p-6 sm:p-8" data-testid="clients-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sales</p>
        <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Clients</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#6D6D70]">
          {isAdmin ? "Organizations with won business across the team." : "Your organizations with won business."}
          {" "}Same record as the prospect — full history preserved.
        </p>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D6D70]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organization, contact name, email or phone..."
            className="rounded-sm pl-9" data-testid="client-search" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          {isAdmin && (
            <Select value={salespersonId} onValueChange={setSalespersonId}>
              <SelectTrigger className="rounded-sm sm:w-44" data-testid="client-filter-salesperson"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All salespeople</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm sm:w-36" data-testid="client-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-sm border border-[#E4E4E7] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#E4E4E7] bg-[#F4F4F5] px-5 py-3 lg:grid">
          <SortHeader label="Organization" sortKey="organization" sort={ls} onSort={onSort} className="col-span-4" testId="client-sort-organization" />
          <SortHeader label="Primary contact" sortKey="contact" sort={ls} onSort={onSort} className="col-span-3" testId="client-sort-contact" />
          <SortHeader label="Salesperson" sortKey="salesperson" sort={ls} onSort={onSort} className="col-span-2" testId="client-sort-salesperson" />
          <SortHeader label="Props" sortKey="properties" sort={ls} onSort={onSort} className="col-span-1 justify-center" testId="client-sort-properties" />
          <SortHeader label="Client since" sortKey="added" sort={ls} onSort={onSort} className="col-span-2 justify-end" testId="client-sort-added" />
        </div>

        {sorted === null ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={24} className="animate-spin text-[#032852]" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="clients-empty">
            <Handshake size={32} className="text-[#032852]/30" />
            <p className="text-sm text-[#6D6D70]">No clients yet. Organizations become clients when an opportunity is closed as Won.</p>
          </div>
        ) : (
          sorted.map((p) => (
            <button key={p.id} onClick={() => navigate(`/prospects/${p.id}`, { state: { from: "clients" } })}
              className="grid w-full grid-cols-2 items-center gap-3 border-b border-[#E4E4E7] px-5 py-4 text-left text-sm transition-colors last:border-0 hover:bg-[#FAFAFA] lg:grid-cols-12"
              data-testid={`client-row-${p.organization_name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
              <div className="col-span-2 min-w-0 lg:col-span-4">
                <p className="truncate font-semibold text-[#09090B]">{p.organization_name}</p>
                <p className="truncate text-xs text-[#6D6D70] lg:hidden">
                  {p.primary_contact ? `${p.primary_contact.first_name} ${p.primary_contact.last_name}` : "—"} · {p.assigned_salesperson_name || "—"}
                </p>
              </div>
              <div className="hidden min-w-0 lg:col-span-3 lg:block">
                <p className="truncate text-[#52525B]">{p.primary_contact ? `${p.primary_contact.first_name} ${p.primary_contact.last_name}` : "—"}</p>
                <p className="truncate text-xs text-[#6D6D70]">{p.primary_contact?.email || "—"}</p>
              </div>
              <div className="hidden truncate text-xs text-[#52525B] lg:col-span-2 lg:block">{p.assigned_salesperson_name || "—"}</div>
              <div className="hidden text-center text-xs font-semibold text-[#032852] lg:col-span-1 lg:block">{p.property_count}</div>
              <div className="hidden text-right lg:col-span-2 lg:block">
                <p className="text-xs text-[#52525B]">{formatDate(p.date_entered)}</p>
                <div className="mt-0.5 flex justify-end"><StatusBadge status={p.status} /></div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
